'use client'

import { useState } from 'react'
import { env } from '~/env'

/**
 * #803 — "Lost your tickets?" for signed-out visitors. Sends the email to
 * the API and shows the same confirmation whether or not a booking exists
 * for that address; the page never renders a ticket from an email alone.
 */
export default function GuestTicketResend({ eventId }: { eventId: string }) {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || state === 'sending') return
    setState('sending')
    try {
      await fetch(`${env.NEXT_PUBLIC_API_URL}/events/${eventId}/guest-ticket-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
    } catch {
      // Same outcome either way — the confirmation copy is deliberately
      // non-committal, so a network blip must not leak a different message.
    }
    setState('sent')
  }

  return (
    <div className="rounded-2xl border border-[#EFE7D7] bg-white p-4 text-sm" data-testid="guest-ticket-resend">
      <p className="font-semibold text-[#2C2D2B]">Lost your tickets?</p>
      {state === 'sent' ? (
        <p className="mt-1 text-[#5E594F]" data-testid="guest-ticket-resend-done">
          If there&apos;s a booking under that email, we&apos;ve sent your tickets to it. Check your
          inbox (and spam folder).
        </p>
      ) : (
        <form onSubmit={submit} className="mt-2 flex flex-col gap-2 sm:flex-row">
          <label className="sr-only" htmlFor="guest-ticket-resend-email">
            Email you booked with
          </label>
          <input
            id="guest-ticket-resend-email"
            type="email"
            required
            autoComplete="email"
            placeholder="Enter the email you booked with"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-full border border-[#E0D4C1] px-4 py-2 text-sm outline-none focus:border-[#DE866D]"
          />
          <button
            type="submit"
            disabled={state === 'sending'}
            className="rounded-full bg-[#2C2D2B] px-4 py-2 text-sm font-semibold text-[#F7F0E6] disabled:opacity-60"
          >
            {state === 'sending' ? 'Sending…' : 'Email my tickets'}
          </button>
        </form>
      )}
    </div>
  )
}
